/* eslint-disable react/no-danger */
import React, {
  useMemo, useContext, useCallback, useRef,
} from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { Typography } from '@material-ui/core';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Popper from '@material-ui/core/Popper';
import Select from '@material-ui/core/Select';
import useQlikSessionObject from 'hooks/useQlikSessionObject';
import languagify from 'utils/languagify';
import useStyles from './styles';
import Slope from '../Charts/Slope';
import Search from '../Search/Search';

import TranslationTxt from '../../survey2022/TranslationTxt/TranslationTxt';
import AppContext from '../../AppContext';

const SlopeSection = ({ header, text }) => {
  // const SlopeSection = () => {
  const { qlikApp } = useContext(AppContext);
  const classes = useStyles();

  const { current: id } = useRef(uuidv4());

  // CHANGE DIMENSION
  const [dimension, setDimension] = React.useState('[Race - Comp]');

  const handleDimensionChange = (event) => {
    setDimension(event.target.value);
  };

  // Get ListObject
  const qFilter = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qInfo: { qType: 'dataFilter' },
      qListObjectDef: {
        qDef: {
          qFieldDefs: [dimension],
        },
        qInitialDataFetch: [{ qWidth: 1, qHeight: 1000 }],
        qShowAlternatives: true,
      },
    },
  });

  const selectionOptions = useMemo(() => {
    if (!qFilter.layout) return [];
    const sortedArray = [];
    const options = qFilter.layout.qListObject.qDataPages[0].qMatrix.map((row) => ({
      qText: languagify(row[0].qText),
      qNum: row[0].qNum,
      qElemNumber: row[0].qElemNumber,
      qState: row[0].qState,
      qFrequency: row[0].qFrequency,
    }));

    sortedArray.push(options.filter((o) => o.qState === 'S'));
    sortedArray.push(options.filter((o) => o.qState !== 'S'));
    return sortedArray.flat();
  }, [qFilter]);

  const selectedVal = useMemo(() => {
    if (!qFilter.layout) return '';
    return qFilter.layout.qListObject.qDataPages[0].qMatrix
      .filter((row) => row[0].qState === 'S')
      .map((row) => row[0]);
  }, [qFilter.layout]);

  // Handle Open
  const handleOpen = useCallback(() => {
    qFilter.model.beginSelections(['/qListObjectDef']);
  }, [qFilter.model]);

  // Handle Close
  const handleClose = useCallback(() => {
    qFilter.model.endSelections(true);
  }, [qFilter.model]);

  const handleChange = useCallback((event, value) => {
    if (!qFilter.model) return;
    const qValues = value.map((val) => val.qElemNumber);
    qFilter.model.selectListObjectValues('/qListObjectDef', qValues, false);
  }, [qFilter.model]);

  return (
    <div className="section" style={{ marginTop: 50 }}>
      <Typography variant="h3" className={classes.header}>
        <TranslationTxt measure={header} />
      </Typography>
      <Typography variant="body1" className={classes.text}>
        <TranslationTxt measure={text} />
      </Typography>
      <Box className={classes.filtersWrap}>
        <Box className={classes.filtersLeft}>
          {/* Dimension */}
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="criteria-select" className={classes.inputLabel}>{languagify('Select Criteria:')}</InputLabel>
            <Select
              labelId="criteria-select"
              id="demo-simple-select"
              value={dimension}
              onChange={handleDimensionChange}
            >
              <MenuItem value="[Race - Comp]">{languagify('Race')}</MenuItem>
              <MenuItem value="[Gender - Comp]">{languagify('Gender')}</MenuItem>
              <MenuItem value="[Job Role - Comp]">{languagify('Job Role')}</MenuItem>
            </Select>
          </FormControl>

          <Box className={classes.filterSearch}>
            <div className={classes.search}>
              <Autocomplete
                multiple
                id={id}
                options={selectionOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.qText}
                getOptionSelected={(option, value) => option.qElemNumber === value.qElemNumber}
                renderTags={(val) => (val.length > 1 ? <span className={classes.selectedSpan}>{`${val.length} ${languagify('Selected')}`}</span> : <span className={classes.selectedSpan}>{languagify(val[0].qText)}</span>)}
                onOpen={handleOpen}
                onClose={handleClose}
                onChange={handleChange}
                value={selectedVal}
                classes={{ inputRoot: classes.inputRoot }}
                PopperComponent={(props) => (<Popper {...props} className={classes.popperOptions} placement="bottom-start" />)}
                renderOption={(option) => (
                  <span className={clsx({ [classes.option]: true, [classes.selected]: option.qState === 'S' })}>
                    {languagify(option.qText)}
                  </span>
                )}
                style={{ minWidth: 120 }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" label={`${languagify('Filter by:')}`} className={classes.inputField} />
                )}
              />

            </div>
          </Box>
        </Box>
        <Search dimension="[Media Outlet Comp]" />
      </Box>
      <Box className={classes.chartsWrapper}>
        <Box className={classes.chartsContainer}>
          <Slope dimension={dimension} />
        </Box>
      </Box>
    </div>
  );
};

SlopeSection.propTypes = {
  header: PropTypes.string,
  text: PropTypes.string,
};

SlopeSection.defaultProps = {
  header: '',
  text: '',
};

export default SlopeSection;
